import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Globe, Moon, Sun, Search, ChevronRight, Menu } from 'lucide-react';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { api, apiError } from '@/lib/api-client';
import type { Language } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { NotificationBell } from '@/components/layout/NotificationBell';

/** Map route segments to breadcrumb labels */
const breadcrumbMap: Record<string, string> = {
  parcels: 'nav.parcels',
  database: 'nav.photos',
  works: 'nav.works',
  import: 'nav.import',
  filter: 'nav.filter',
  admin: 'nav.admin',
  users: 'nav.admin.users',
  audit: 'nav.admin.audit',
  settings: 'nav.admin.settings',
  profile: 'auth:profile.title',
};

export function Header({ onMobileMenuToggle }: { onMobileMenuToggle?: () => void } = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const pushToast = useUiStore((s) => s.pushToast);

  const onLanguageChange = (lng: Language) => {
    setLanguage(lng);
    void i18n.changeLanguage(lng);
  };

  const onLogout = async () => {
    try {
      await api.post('auth/sign-out');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  };

  // Build breadcrumbs from path
  const pathSegments = location.pathname
    .replace(/^\/app\/?/, '')
    .split('/')
    .filter(Boolean);

  return (
    <header className="flex h-14 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:px-4 dark:border-slate-700 dark:bg-slate-900 dark:shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
      {/* Left: hamburger + breadcrumb */}
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <button
          type="button"
          onClick={onMobileMenuToggle}
          className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 lg:hidden dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={t('nav.openMenu')}
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="hidden font-semibold text-forest-700 sm:inline dark:text-forest-300">{t('app.title')}</span>
        <Link to="/app/beta" className="hidden rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase leading-tight text-amber-700 transition-colors hover:bg-amber-200 sm:inline dark:bg-amber-500/20 dark:text-amber-300 dark:hover:bg-amber-500/30">Beta</Link>
        {pathSegments.length > 0 && (
          <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
            {pathSegments.map((segment, idx) => {
              const labelKey = breadcrumbMap[segment];
              return (
                <span key={idx} className="flex min-w-0 items-center gap-1.5">
                  <ChevronRight className="hidden h-3.5 w-3.5 flex-shrink-0 text-slate-300 sm:inline dark:text-slate-600" />
                  <span className="truncate text-slate-500 dark:text-slate-400">
                    {labelKey ? t(labelKey) : segment}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: controls */}
      <div className="flex flex-shrink-0 items-center gap-1.5">
        {/* Language selector */}
        <div className="flex items-center gap-1 rounded-md border border-slate-150 px-2 py-1 dark:border-slate-700">
          <Globe className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden />
          <select
            aria-label="language"
            value={language}
            onChange={(e) => onLanguageChange(e.target.value as Language)}
            className="bg-transparent text-xs text-slate-600 focus:outline-none dark:text-slate-300"
          >
            <option value="ro">{t('language.ro')}</option>
            <option value="ru">{t('language.ru')}</option>
            <option value="en">{t('language.en')}</option>
          </select>
        </div>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={t('theme.toggle')}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Notifications */}
        {user ? <NotificationBell /> : null}

        {/* User info */}
        {user ? (
          <div className="ml-1.5 flex items-center gap-2 border-l border-slate-200 pl-3 dark:border-slate-700">
            <button
              type="button"
              onClick={() => navigate('/app/profile')}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-forest-100 text-xs font-bold text-forest-700 transition-colors hover:bg-forest-200 dark:bg-forest-900/40 dark:text-forest-300 dark:hover:bg-forest-900/60"
              title={t('auth:profile.title')}
            >
              {(user.fullName || user.username || 'U').charAt(0).toUpperCase()}
            </button>
            <button
              type="button"
              onClick={() => navigate('/app/profile')}
              className="hidden text-sm font-medium text-slate-700 transition-colors hover:text-forest-700 sm:inline dark:text-slate-300 dark:hover:text-forest-300"
            >
              {user.fullName}
            </button>
            <button
              type="button"
              onClick={() => void onLogout()}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              aria-label={t('action.logout')}
              title={t('action.logout')}
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
